
import React from 'react';
import StaticPageLayout from '@/components/layout/StaticPageLayout';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  return (
    <StaticPageLayout 
      title="Privacy Policy" 
      description="Last Updated: April 25, 2025"
      className="prose-h2:text-2xl prose-h2:font-medium prose-h2:mt-12 prose-h2:mb-6 prose-p:mb-4 prose-ul:mb-6 prose-ul:list-disc prose-ul:pl-6"
    >
      <nav className="mb-12 p-6 bg-secondary/50 rounded-lg">
        <h2 className="text-lg font-medium mb-4">Table of Contents</h2>
        <ul className="space-y-2 list-none pl-0">
          <li><a href="#introduction" className="text-foreground/80 hover:text-foreground no-underline">1. Introduction</a></li>
          <li><a href="#information-collection" className="text-foreground/80 hover:text-foreground no-underline">2. Information We Collect</a></li>
          <li><a href="#information-use" className="text-foreground/80 hover:text-foreground no-underline">3. How We Use Your Information</a></li>
          <li><a href="#information-sharing" className="text-foreground/80 hover:text-foreground no-underline">4. Information Sharing and Disclosure</a></li>
          <li><a href="#your-rights" className="text-foreground/80 hover:text-foreground no-underline">5. Your Rights and Choices</a></li>
          <li><a href="#data-security" className="text-foreground/80 hover:text-foreground no-underline">6. Data Security</a></li>
          <li><a href="#children" className="text-foreground/80 hover:text-foreground no-underline">7. Children's Privacy</a></li>
          <li><a href="#international" className="text-foreground/80 hover:text-foreground no-underline">8. International Data Transfers</a></li>
          <li><a href="#changes" className="text-foreground/80 hover:text-foreground no-underline">9. Changes to This Privacy Policy</a></li>
          <li><a href="#contact" className="text-foreground/80 hover:text-foreground no-underline">10. Contact Us</a></li>
        </ul>
      </nav>

      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          Welcome to Everhouse ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
        </p>
        <p>
          By using our website and services, you agree to the collection and use of information in accordance with this policy.
        </p>
      </section>

      <section id="information-collection">
        <h2>2. Information We Collect</h2>
        <h3 className="text-xl font-medium mt-8 mb-4">Information You Provide to Us</h3>
        <ul>
          <li>Account information (name, email address, password)</li>
          <li>Profile information (biography, profile picture)</li>
          <li>Payment information (processed securely through our payment processors)</li>
          <li>Communication preferences</li>
          <li>Content you create, share, or upload</li>
        </ul>

        <h3 className="text-xl font-medium mt-8 mb-4">Information We Collect Automatically</h3>
        <ul>
          <li>Device information (IP address, browser type, operating system)</li>
          <li>Usage data (pages visited, time spent, clicks)</li>
          <li>Location data (if permitted by your device)</li>
          <li>Cookies and similar tracking technologies</li>
        </ul>
      </section>

      <section id="information-use">
        <h2>3. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and improve our services</li>
          <li>Process your transactions and send transaction notifications</li>
          <li>Communicate with you about our services, updates, and promotions</li>
          <li>Personalize your experience and deliver content tailored to your interests</li>
          <li>Monitor and analyze trends, usage, and activities</li>
          <li>Detect, prevent, and address technical issues and security breaches</li>
        </ul>
      </section>

      <section id="information-sharing">
        <h2>4. Information Sharing and Disclosure</h2>
        <p>We may share your information with:</p>
        <ul>
          <li>Service providers who assist in our operations</li>
          <li>Professional advisors (lawyers, accountants, auditors)</li>
          <li>Law enforcement or regulatory bodies when required by law</li>
          <li>Business partners with your consent</li>
        </ul>
      </section>

      <section id="your-rights">
        <h2>5. Your Rights and Choices</h2>
        <p>You have the right to:</p>
        <ul>
          <li>Access, correct, or delete your personal information</li>
          <li>Object to or restrict certain processing of your data</li>
          <li>Data portability (receive your data in a structured format)</li>
          <li>Withdraw consent where processing is based on consent</li>
          <li>Opt-out of marketing communications</li>
        </ul>
      </section>

      <section id="data-security">
        <h2>6. Data Security</h2>
        <p>
          We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure.
        </p>
      </section>

      <section id="children">
        <h2>7. Children's Privacy</h2>
        <p>
          Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us.
        </p>
      </section>

      <section id="international">
        <h2>8. International Data Transfers</h2>
        <p>
          Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws than your country.
        </p>
      </section>

      <section id="changes">
        <h2>9. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top.
        </p>
      </section>

      <section id="contact">
        <h2>10. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy, please contact us at{' '}
          <Link to="/contact" className="text-foreground hover:text-foreground/80">
            our contact page
          </Link>.
        </p>
      </section>
    </StaticPageLayout>
  );
};

export default PrivacyPolicyPage;
